import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Grid, 
  Paper, 
  TextField, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Link,
  Autocomplete,
  Container,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import { 
  OpenInNew as OpenInNewIcon,
  LocationOn as LocationOnIcon,
  Search as SearchIcon,
  PersonAdd as PersonAddIcon,
  Close as CloseIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import debounce from 'lodash/debounce';
import { toast } from 'react-toastify';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import PageHeader from '../../components/layout/PageHeader';
import '../../styles/CreateOrder.css';
import api from '../../services/apiClient';
import authService from '../../services/authService';
import { getWaitingLocations } from '../../services/waitingLocationService';
import { getCastWaitingLocations } from '../../services/castWaitingLocationService';
import { getCustomerOrders } from '../../services/customer';

const orderOptions = [
  { value: 'honmei', label: '本指名' },
  { value: 'shimei', label: '指名' },
  { value: 'free', label: 'フリー' }
];

const CreateOrder = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // User and Auth states
  const [currentUser, setCurrentUser] = useState<any | null>(null);
  const [users, setUsers] = useState<any[]>([]);

  // Form states
  const [formData, setFormData] = useState({
    customer_id: '',
    customer_phone: '',
    cast_ids: [] as string[],
    user_id: '',
    driver_id: '',
    hotel_name: '',
    hotel_id: null as number | null,
    hotel_address: '',
    room_number: '',
    pickup_lat: null as number | null,
    pickup_lng: null as number | null,
    pickup_no_location: false, // 迎え場所なし（担当ドライバーと同位置）
    hotel_lat: null as number | null,
    hotel_lng: null as number | null,
    reservation_date: '',
    reservation_time: '',
    order_type: 'free',
    status: '予約',
    course: '60分',
    options: [] as string[],
    entry_time: '',
    discount: 0,
    description: '',
    special_instructions: '',
    pickup_address: '',
    delivery_address: '',
    service_time: 0,
    option_details: [] as { id?: number; name: string; castback: number; profit: number }[]
  });
  const [systemSettings, setSystemSettings] = useState<any | null>(null);
  const [coursesOptions, setCoursesOptions] = useState<any[]>([]);

  // Customer related states
  const [customerPhoneSearch, setCustomerPhoneSearch] = useState('');
  const [foundCustomer, setFoundCustomer] = useState<any | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [customerStatus, setCustomerStatus] = useState('');
  const [isCustomerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [orderHistory, setOrderHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Cast and Driver states
  const [availableCasts, setAvailableCasts] = useState<any[]>([]);
  const [selectedCast, setSelectedCast] = useState<any | null>(null);
  const [isLoadingCasts, setIsLoadingCasts] = useState(false);
  const [availableDrivers, setAvailableDrivers] = useState<any[]>([]);
  const [selectedDriver, setSelectedDriver] = useState<any | null>(null);
  const [isLoadingDrivers, setIsLoadingDrivers] = useState(false);
  
  const [hotelOptions, setHotelOptions] = useState<any[]>([]);
  const [loadingHotels, setLoadingHotels] = useState(false);
  const [optionChoices, setOptionChoices] = useState<any[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<any | null>(null);
  const [calculatedTotals, setCalculatedTotals] = useState({
    castback: 0,
    revenue: 0,
    grandTotal: 0
  });
  const [isManuallyEdited, setIsManuallyEdited] = useState({
    castback: false,
    revenue: false
  });

  const theme = useTheme();

  const cleanName = (name: string) => {
    if (!name) return '';
    return name.split(' ')
      .filter(part => part.toLowerCase() !== 'nan')
      .join(' ')
      .trim();
  };

  // ユーザー名を適切に表示するヘルパー関数
  const formatUserDisplayName = (user: any): string => {
    // バックエンドから正しいデータが来るようになったのでシンプルに処理
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    
    if (user.first_name) {
      return user.first_name;
    }
    
    // フォールバック
    return user.username || `ユーザー${user.id}`;
  };

  const checkUserPermission = useCallback(() => {
    const userInfo = authService.getUserInfo();
    if (!userInfo) {
      toast.error('ログインしてください');
      navigate('/login');
      return false;
    }
    const userRole = userInfo.user?.role || userInfo.role || userInfo.role_name;
    if (!userRole || !['ADMIN', 'MANAGER', 'DISPATCHER'].includes(userRole.toUpperCase())) {
      toast.warning('オーダー作成を行う権限がありません');
      navigate('/login');
      return false;
    }
    setCurrentUser(userInfo);
    return true;
  }, [navigate]);

  useEffect(() => {
    const initialize = async () => {
      if (!checkUserPermission()) {
        setLoading(false);
        return;
      }
      try {
        const usersResponse = await api.get('/users');
        if(usersResponse.data && Array.isArray(usersResponse.data.users)) {
            setUsers(usersResponse.data.users);
        }

        const settingsResponse = await api.get('/system/settings');
        if (settingsResponse.data && settingsResponse.data.data) {
          const settings = settingsResponse.data.data;
          setSystemSettings(settings);
          if (settings.courses) {
              setCoursesOptions(settings.courses.map((c: any) => ({ value: c.id, label: `${c.duration}分`, course: c })))
          }
        }

        const optionsResponse = await api.get('/system/options');
        if (Array.isArray(optionsResponse.data)) {
          setOptionChoices(optionsResponse.data);
        }

      } catch (err) {
        console.error('初期化中にエラーが発生しました:', err);
        setError('データの取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };
    initialize();
  }, [checkUserPermission]);

  // 全てのホテルオプションを保持するstate
  const [allHotelOptions, setAllHotelOptions] = useState<any[]>([]);

  // 割り当てキャストのNGホテル情報（cast_id -> NGホテルの配列）
  const [castNgHotels, setCastNgHotels] = useState<Record<string, { hotel_id: number | null; hotel_name: string }[]>>({});

  // 迎え場所の候補データ
  const [waitingMaster, setWaitingMaster] = useState<any[]>([]); // 待機場所マスタ
  const [castWaiting, setCastWaiting] = useState<Record<string, any[]>>({}); // cast_id -> 個別待機場所
  const [selectedPickup, setSelectedPickup] = useState<any | null>(null); // 迎え場所Autocompleteの選択値
  const [pickupGeocoding, setPickupGeocoding] = useState(false); // 住所→座標取得中

  // ルート/ETA（担当ドライバー現在地→迎え場所→ホテル）
  const [routeInfo, setRouteInfo] = useState<any | null>(null); // {duration, distance, legs, estimated, legLabels}
  const [routeLoading, setRouteLoading] = useState(false);
  const [routeError, setRouteError] = useState<string | null>(null);

  useEffect(() => {
    // コンポーネントのマウント時に一度だけ全ホテルを取得
    const fetchAllHotels = async () => {
      setLoadingHotels(true);
      try {
        const response = await api.get('/system/hotels');
        if (response.data?.success && Array.isArray(response.data.data)) {
          setAllHotelOptions(response.data.data);
          setHotelOptions(response.data.data); // 初期表示用にセット
        } else {
          setAllHotelOptions([]);
          setHotelOptions([]);
        }
      } catch (error) {
        console.error("ホテルの取得に失敗しました:", error);
        setAllHotelOptions([]);
        setHotelOptions([]);
      } finally {
        setLoadingHotels(false);
      }
    };
    fetchAllHotels();
  }, []);

  // 待機場所マスタをマウント時に取得
  useEffect(() => {
    (async () => {
      try {
        const list = await getWaitingLocations();
        setWaitingMaster(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error('待機場所マスタの取得に失敗:', e);
        setWaitingMaster([]);
      }
    })();
  }, []);

  // 割り当てキャストの個別待機場所を取得（cast_ids が変わるたびに差分取得）
  useEffect(() => {
    const castIds = formData.cast_ids;
    const missing = castIds.filter((cid) => !(cid in castWaiting));
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      const updates: Record<string, any[]> = {};
      await Promise.all(
        missing.map(async (cid) => {
          try {
            const list = await getCastWaitingLocations(Number(cid));
            updates[cid] = Array.isArray(list) ? list : [];
          } catch (e) {
            console.error(`キャスト${cid}の待機場所取得に失敗:`, e);
            updates[cid] = [];
          }
        })
      );
      if (!cancelled) setCastWaiting((prev) => ({ ...prev, ...updates }));
    })();
    return () => { cancelled = true; };
  }, [formData.cast_ids, castWaiting]);

  // 迎え場所の統合候補（待機場所マスタ + 登録ホテル + 選択キャストの個別待機場所 + 「迎え場所なし」）
  const pickupOptions = useMemo(() => {
    const opts: any[] = [];
    // 迎え場所なし（担当ドライバーと同位置）
    opts.push({ source: 'none', id: 'none', name: '迎え場所なし（ドライバーと同位置）', name_kana: '', address: '', latitude: null, longitude: null });
    // 待機場所マスタ
    for (const w of waitingMaster) {
      opts.push({ source: 'waiting', id: `w-${w.id}`, name: w.name, name_kana: w.name_kana || '', address: w.address || '', latitude: w.latitude, longitude: w.longitude });
    }
    // 選択中キャストの個別待機場所
    for (const cid of formData.cast_ids) {
      for (const w of (castWaiting[cid] || [])) {
        opts.push({ source: 'cast', id: `c-${cid}-${w.id}`, name: w.name || w.address || '待機場所', name_kana: w.name_kana || '', address: w.address || '', latitude: w.latitude, longitude: w.longitude });
      }
    }
    // 登録ホテル
    for (const h of allHotelOptions) {
      opts.push({ source: 'hotel', id: `h-${h.id}`, name: h.name, name_kana: h.name_kana || '', address: h.address || '', latitude: h.latitude, longitude: h.longitude });
    }
    return opts;
  }, [waitingMaster, castWaiting, allHotelOptions, formData.cast_ids]);

  // 迎え場所候補のかな検索フィルタ（ホテルと同じ toHiragana ロジック）
  const filterPickupOptions = (options: any[], { inputValue }: { inputValue: string }) => {
    if (!inputValue) return options;
    const q = toHiragana(inputValue.toLowerCase());
    return options.filter((o) => {
      if (o.source === 'none') return true; // 「迎え場所なし」は常に候補に残す
      const name = (o.name || '').toLowerCase();
      const kana = (o.name_kana || '').toLowerCase();
      const addr = (o.address || '').toLowerCase();
      return name.includes(q) || kana.includes(q) || toHiragana(kana).includes(q) || addr.includes(q);
    });
  };

  // 迎え場所のカテゴリラベル
  const pickupSourceLabel = (source: string) => {
    switch (source) {
      case 'waiting': return '待機';
      case 'hotel': return 'ホテル';
      case 'cast': return 'キャスト';
      case 'none': return '';
      default: return '';
    }
  };

  // 迎え場所を選択したときの処理
  const handleSelectPickup = (opt: any | null) => {
    setSelectedPickup(opt);
    if (!opt) {
      setFormData((prev) => ({ ...prev, pickup_address: '', pickup_lat: null, pickup_lng: null, pickup_no_location: false }));
      return;
    }
    if (opt.source === 'none') {
      // 迎え場所なし（ドライバーと同位置）
      setFormData((prev) => ({ ...prev, pickup_address: '迎え場所なし（ドライバーと同位置）', pickup_lat: null, pickup_lng: null, pickup_no_location: true }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      pickup_address: opt.address || opt.name || '',
      pickup_lat: opt.latitude ?? null,
      pickup_lng: opt.longitude ?? null,
      pickup_no_location: false,
    }));
  };

  // 未登録の住所から座標を取得（Google/Nominatim）
  const handleGeocodePickup = async () => {
    const address = (formData.pickup_address || '').trim();
    if (!address) {
      toast.error('住所を入力してください');
      return;
    }
    setPickupGeocoding(true);
    try {
      const res = await api.get('/geocode', { params: { address } });
      const loc = res.data?.location;
      if (loc && loc.lat != null && loc.lng != null) {
        setFormData((prev) => ({ ...prev, pickup_lat: loc.lat, pickup_lng: loc.lng, pickup_no_location: false }));
        toast.success('座標を取得しました');
      } else {
        toast.error('座標を取得できませんでした');
      }
    } catch (e) {
      console.error('座標取得に失敗:', e);
      toast.error('座標の取得に失敗しました');
    } finally {
      setPickupGeocoding(false);
    }
  };

  // 秒数を「約◯分」表記にする
  const fmtMinutes = (sec: number) => {
    const m = Math.round((sec || 0) / 60);
    if (m < 60) return `約${m}分`;
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return `約${h}時間${mm > 0 ? `${mm}分` : ''}`;
  };

  // ルート/ETAを計算する。
  // 地点: [担当ドライバー現在地(取得できれば)] → [迎え場所(あれば)] → [ホテル]
  // ドライバー現在地が取れない場合は「迎え場所→ホテル」。迎え場所なし＋現在地なしは計算不可。
  const handleCalcRoute = async () => {
    setRouteError(null);
    setRouteInfo(null);

    // ホテル座標は必須
    if (formData.hotel_lat == null || formData.hotel_lng == null) {
      setRouteError('ホテルの座標がありません（登録済みホテルを選択してください）');
      return;
    }

    // 担当ドライバー（入室担当＝driver_id）
    const driverId = formData.driver_id ? Number(formData.driver_id) : null;

    // 迎え場所の座標（「迎え場所なし」のときは無し）
    const hasPickup = !formData.pickup_no_location && formData.pickup_lat != null && formData.pickup_lng != null;

    // ラベル用に、どの地点が含まれるかを組み立てる
    const legLabels: string[] = [];
    const points: { lat: number; lng: number }[] = [];
    if (hasPickup) {
      points.push({ lat: Number(formData.pickup_lat), lng: Number(formData.pickup_lng) });
    }
    points.push({ lat: Number(formData.hotel_lat), lng: Number(formData.hotel_lng) });

    // 迎え場所なしで、かつドライバー現在地も使えない場合は計算不可
    if (!hasPickup && !driverId) {
      setRouteError('迎え場所なしの場合は担当ドライバーを選択してください（現在地から計算します）');
      return;
    }

    setRouteLoading(true);
    try {
      // driver_id を渡すとバックエンドが先頭にドライバー最新位置を自動付与する。
      // 現在地が無ければ付与されず、points（迎え→ホテル or ホテルのみ）で計算される。
      const res = await api.post('/gps/route', {
        driver_id: driverId,
        include_driver: !!driverId,
        points,
      });
      const data = res.data;
      if (!data?.success) {
        setRouteError(data?.error || 'ルートを取得できませんでした');
        return;
      }

      // legs の件数から区間ラベルを推定する。
      // バックエンドは driver_id の現在地を先頭に付けるため、legs 数で判定する。
      const legs = data.legs || [];
      // 区間ラベルを作る: 先頭にドライバーが付いたかは legs 数 と points 数で判定
      // points 数(迎えあり=2, なし=1) に対し、legs がそれより1多ければ先頭にドライバーが付いている
      const driverIncluded = legs.length === points.length; // points間のleg数 = points-1。ドライバー付与で +1地点 → leg数 = points
      const labels: string[] = [];
      let idx = 0;
      if (driverIncluded && legs.length > 0) {
        // ドライバー→(迎え or ホテル)
        labels.push(`現在地→${hasPickup ? '迎え' : 'ホテル'}: ${fmtMinutes(legs[idx].duration)}`);
        idx++;
      }
      if (hasPickup && idx < legs.length) {
        labels.push(`迎え→ホテル: ${fmtMinutes(legs[idx].duration)}`);
        idx++;
      }

      setRouteInfo({
        duration: data.duration,
        distance: data.distance,
        estimated: data.estimated,
        legLabels: labels,
        driverIncluded,
      });
    } catch (e) {
      console.error('ルート計算に失敗:', e);
      setRouteError('ルート計算に失敗しました');
    } finally {
      setRouteLoading(false);
    }
  };

  // 割り当てキャストのNGホテルを取得（cast_ids が変わるたびに差分取得）
  useEffect(() => {
    const castIds = formData.cast_ids;
    const missing = castIds.filter((cid) => !(cid in castNgHotels));
    if (missing.length === 0) return;

    let cancelled = false;
    (async () => {
      const updates: Record<string, { hotel_id: number | null; hotel_name: string }[]> = {};
      await Promise.all(
        missing.map(async (cid) => {
          try {
            const res = await api.get(`/users_edit/${cid}/ng_hotels`);
            const list = Array.isArray(res.data) ? res.data : [];
            updates[cid] = list.map((h: any) => ({
              hotel_id: h.hotel_id ?? null,
              hotel_name: h.hotel_name || '',
            }));
          } catch (error) {
            console.error(`キャスト${cid}のNGホテル取得に失敗:`, error);
            updates[cid] = [];
          }
        })
      );
      if (!cancelled) setCastNgHotels((prev) => ({ ...prev, ...updates }));
    })();

    return () => {
      cancelled = true;
    };
  }, [formData.cast_ids, castNgHotels]);

  // 選択中ホテルが、割り当てキャストのいずれかのNGホテルに該当するか判定。
  // 該当するキャスト名の配列を返す（空なら警告なし）。
  const ngHotelConflicts = useMemo(() => {
    if (!formData.hotel_name && !formData.hotel_id) return [] as string[];
    const conflicts: string[] = [];
    for (const cid of formData.cast_ids) {
      const ngList = castNgHotels[cid] || [];
      const hit = ngList.some((ng) => {
        if (formData.hotel_id != null && ng.hotel_id != null) {
          return ng.hotel_id === formData.hotel_id;
        }
        // id が無い場合は名前で突合
        return !!formData.hotel_name && ng.hotel_name === formData.hotel_name;
      });
      if (hit) {
        const cast = availableCasts.find((c) => c.id.toString() === cid);
        conflicts.push(cast ? formatUserDisplayName(cast) : `キャストID:${cid}`);
      }
    }
    return conflicts;
  }, [formData.hotel_name, formData.hotel_id, formData.cast_ids, castNgHotels, availableCasts]);

  const filterHotelOptions = (options: any[], { inputValue }: { inputValue: string }) => {
    const hiraganaInput = toHiragana(inputValue.toLowerCase());

    return options.filter(option => {
      const name = option.name?.toLowerCase() || '';
      const nameKana = option.name_kana?.toLowerCase() || '';
      const nameKanaHiragana = toHiragana(nameKana);

      return name.includes(hiraganaInput) || 
             nameKana.includes(hiraganaInput) ||
             nameKanaHiragana.includes(hiraganaInput);
    });
  };

  // カタカナをひらがなに変換するヘルパー関数
  const toHiragana = (str: string) => {
    return str.replace(/[\u30a1-\u30f6]/g, (match) => {
      return String.fromCharCode(match.charCodeAt(0) - 0x60);
    });
  };

  const fetchAvailableUsers = useCallback(async (role: 'cast' | 'driver') => {
    if (!formData.reservation_date || !formData.reservation_time) {
      return;
    }
    
    const setIsLoading = role === 'cast' ? setIsLoadingCasts : setIsLoadingDrivers;
    const setAvailable = role === 'cast' ? setAvailableCasts : setAvailableDrivers;
    setIsLoading(true);

    try {
      const dateTimeString = `${formData.reservation_date}T${formData.reservation_time}`;
      const response = await api.get('/users_edit/available', { 
        params: { 
          datetime: dateTimeString,
          role: role.toUpperCase() 
        }
      });
      
      if (response.data && Array.isArray(response.data)) {
          setAvailable(response.data);
      } else {
          setAvailable([]);
      }
    } catch (error) {
        console.error(`利用可能な${role}の取得に失敗しました:`, error);
        setAvailable([]);
    } finally {
        setIsLoading(false);
    }
  }, [formData.reservation_date, formData.reservation_time]);

  useEffect(() => {
    if (formData.reservation_date && formData.reservation_time) {
      fetchAvailableUsers('cast');
      fetchAvailableUsers('driver');
    }
  }, [formData.reservation_date, formData.reservation_time, fetchAvailableUsers]);

  const handleCustomerPhoneSearch = useCallback(debounce(async (phone) => {
    if (!phone || phone.length < 6) {
      setFoundCustomer(null);
      setCustomerStatus('');
      return;
    }
    setIsSearching(true);
    try {
      const response = await api.get(`/customers/search`, { params: { phone } });
      if (response.data.status === 'not_found') {
        setFoundCustomer({ phone });
        setCustomerStatus('新規');
        setFormData(prev => ({ ...prev, customer_id: '' }));
      } else {
        setFoundCustomer(response.data);
        const statusText = response.data.ng_casts?.length > 0 ? `履歴あり NG：${response.data.ng_casts.join('、')}` : '履歴あり';
        setCustomerStatus(statusText);
        setFormData(prev => ({ ...prev, customer_id: response.data.id }));
      }
    } catch (error) {
      console.error('顧客検索に失敗しました:', error);
      setFoundCustomer(null);
      setCustomerStatus('');
    } finally {
      setIsSearching(false);
    }
  }, 500), []);
  
  const handlePhoneInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const phone = e.target.value;
    setCustomerPhoneSearch(phone);
    handleCustomerPhoneSearch(phone);
  };

  // 顧客情報ダイアログを開く。既存顧客ならオーダー履歴も取得して表示する。
  const handleOpenCustomerDialog = async () => {
    setCustomerDialogOpen(true);
    // 履歴取得に使う user_id。search レスポンスは user_id を明示的に返す（無ければ id を使う）
    const uid = foundCustomer?.user_id ?? foundCustomer?.id;
    if (!uid) {
      setOrderHistory([]);
      return;
    }
    setLoadingHistory(true);
    try {
      // 完了のみ（入室時間あり）を取得する
      const res = await getCustomerOrders(Number(uid), true);
      setOrderHistory(Array.isArray(res?.orders) ? res.orders : []);
    } catch (e) {
      console.error('顧客オーダー履歴の取得に失敗:', e);
      setOrderHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleCreateNewCustomer = async () => {
    if (!customerPhoneSearch) {
      toast.error('電話番号を入力してください');
      return;
    }
    try {
      const cleanPhone = customerPhoneSearch.replace(/-/g, '');
      const newCustomerData = { phone_number: cleanPhone, name: '' };
      const response = await api.post(`/customers`, newCustomerData);
      if (response.data && response.data.id) {
        setFoundCustomer(response.data);
        setCustomerStatus('新規登録完了');
        setFormData(prev => ({ ...prev, customer_id: response.data.id, customer_phone: response.data.phone_number }));
        toast.success('新規顧客を登録しました');
      } else {
        toast.error('顧客の作成に失敗しました');
      }
    } catch (error) {
      console.error('顧客作成エラー', error);
      toast.error('顧客の作成中にエラーが発生しました');
    }
  };

  useEffect(() => {
    const calculateTotals = () => {
      let totalCastback = 0;
      let totalRevenue = 0;

      // Course calculation
      if (selectedCourse) {
        totalCastback += selectedCourse.castback || 0;
        totalRevenue += selectedCourse.revenue || 0;
      }

      // Options calculation
      formData.option_details.forEach(option => {
        totalCastback += option.castback || 0;
        totalRevenue += option.profit || 0;
      });

      // 担当キャストの指名料・本指名料をキャストバックに加算する。
      // order_type が 'nomination'(指名) なら nomination_fee、
      // 'direct_nomination'(本指名) なら special_nomination_fee を、
      // 選択されている全キャスト分合計する。フリーの場合は加算しない。
      if (formData.order_type === 'nomination' || formData.order_type === 'direct_nomination') {
        const feeKey = formData.order_type === 'nomination' ? 'nomination_fee' : 'special_nomination_fee';
        const selectedCasts = availableCasts.filter(c => formData.cast_ids.includes(c.id.toString()));
        selectedCasts.forEach(cast => {
          totalCastback += cast[feeKey] || 0;
        });
      }

      // Apply discount
      totalRevenue -= formData.discount || 0;
      
      const grandTotal = totalCastback + totalRevenue;

      setCalculatedTotals(prev => ({
        castback: isManuallyEdited.castback ? prev.castback : totalCastback,
        revenue: isManuallyEdited.revenue ? prev.revenue : totalRevenue,
        grandTotal: (isManuallyEdited.castback ? prev.castback : totalCastback) + (isManuallyEdited.revenue ? prev.revenue : totalRevenue),
      }));
    };

    calculateTotals();
  }, [selectedCourse, formData.option_details, formData.discount, formData.order_type, formData.cast_ids, availableCasts, isManuallyEdited]);

  const handleOptionChange = (checked: boolean, option: any) => {
    // Reset manual edit flags when options change
    setIsManuallyEdited({ castback: false, revenue: false });
    setFormData(prev => {
      const currentOptionIds = prev.options || [];
      const currentOptionDetails = prev.option_details || [];

      let newOptionIds: string[];
      let newOptionDetails: any[];

      if (checked) {
        newOptionIds = [...currentOptionIds, option.id.toString()];
        newOptionDetails = [...currentOptionDetails, {
          name: option.name,
          castback: option.castback,
          profit: option.price - option.castback
        }];
      } else {
        newOptionIds = currentOptionIds.filter(id => id !== option.id.toString());
        newOptionDetails = currentOptionDetails.filter(detail => detail.name !== option.name);
      }
      
      return { ...prev, options: newOptionIds, option_details: newOptionDetails };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.reservation_time) {
      toast.error('予約時間を入力してください');
      return;
    }
    // 顧客が未確定(新規顧客を「登録」していない、または既存顧客を検索・選択していない)のまま
    // 送信されるケースを事前に防ぐ。customer_idが空だとバックエンドで400エラーになり、
    // どの項目が不足しているか分かりにくいため、ここで明示的に案内する。
    if (!formData.customer_id) {
      toast.error('顧客が未登録です。電話番号で検索し、新規の場合は「新規顧客を登録」してから作成してください');
      return;
    }
    if (!formData.pickup_address) {
      toast.error('送迎先を入力してください');
      return;
    }
    if (!(formData.hotel_address || formData.delivery_address)) {
      toast.error('ホテル住所を入力してください');
      return;
    }
    try {
      const orderData = {
        ...formData,
        ...calculatedTotals, // 計算された料金情報を追加
        driver_id: formData.driver_id, // formDataから直接使用
        delivery_address: formData.hotel_address || formData.delivery_address,
        course: selectedCourse ? selectedCourse.duration : formData.service_time,
        user_id: currentUser?.user?.id, // オーダー作成者のIDを currentUser.user.id から取得
        status: formData.status, // ステータスを追加
      };

      // 不要なキーを削除
      delete orderData.customer_phone;
      delete orderData.description;
      // hotel_id はフロントのNGホテル判定用。オーダー保存には送らない
      delete (orderData as any).hotel_id;
      // 迎え場所・ホテルの座標、フラグはフロント内部用（ルート計算に使う）。オーダー保存には送らない
      delete (orderData as any).pickup_lat;
      delete (orderData as any).pickup_lng;
      delete (orderData as any).pickup_no_location;
      delete (orderData as any).hotel_lat;
      delete (orderData as any).hotel_lng;
      
      const response = await api.post('/orders', orderData);
      if (response.data) {
        toast.success('オーダーが正常に作成されました');
        navigate('/orders');
      } else {
        toast.warning('オーダーは作成されましたが、詳細情報が取得できませんでした');
        navigate('/orders');
      }
    } catch (error: any) {
      console.error('オーダー作成中にエラーが発生しました:', error);
      const responseData = error.response?.data;
      let errorMessage = responseData?.message || responseData?.error || '不明なエラーが発生しました';
      // バックエンドが不足フィールド名を返す場合は、具体的に表示する
      if (Array.isArray(responseData?.missing_fields) && responseData.missing_fields.length > 0) {
        const fieldLabels: Record<string, string> = {
          customer_id: '顧客',
          pickup_address: '送迎先',
          delivery_address: 'ホテル住所',
        };
        const labels = responseData.missing_fields.map((f: string) => fieldLabels[f] || f).join('、');
        errorMessage = `${errorMessage}（不足項目: ${labels}）`;
      }
      toast.error(`オーダー作成に失敗しました: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };
  
  const renderUserSelect = (role: 'cast' | 'driver') => {
    const availableUsers = role === 'cast' ? availableCasts : availableDrivers;
    const isLoading = role === 'cast' ? isLoadingCasts : isLoadingDrivers;
    
    const isMultiple = role === 'cast';

    const handleChange = (event: any, newValue: any | any[]) => {
      if (role === 'cast') {
        const selectedCasts = newValue as any[];
        const newIds = selectedCasts.map(v => v.id.toString());
        // setSelectedCast(selectedCasts); // 複数選択なので、selectedCast state は使わない or 配列を保持する
        setFormData(prev => ({ ...prev, cast_ids: newIds }));
      } else {
        const selected = newValue as any;
        setSelectedDriver(selected);
        setFormData(prev => ({ ...prev, driver_id: selected ? selected.id.toString() : '' }));
      }
    };
    
    const getOptionLabel = (option: any) => formatUserDisplayName(option);

    let value = null;
    if (role === 'cast') {
      value = availableUsers.filter(u => formData.cast_ids.includes(u.id.toString()));
    } else {
      value = selectedDriver || availableUsers.find(u => u.id.toString() === formData.driver_id) || null;
    }
    
    return (
      <Autocomplete
        multiple={isMultiple}
        options={availableUsers}
        loading={isLoading}
        getOptionLabel={getOptionLabel}
        onChange={handleChange}
        value={value}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <TextField
            {...params}
            label={`担当${role === 'cast' ? 'キャスト' : 'ドライバー'}`}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {getOptionLabel(option)}
            </li>
          )}
      />
    );
  };
  
  if (loading) {
    return <DashboardLayout title="オーダー作成"><Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh"><CircularProgress /></Box></DashboardLayout>;
  }

  if (error) {
    return <DashboardLayout title="オーダー作成"><Box p={3}><Alert severity="error">{error}</Alert></Box></DashboardLayout>;
  }

  return (
    <DashboardLayout title="オーダー作成">
      <PageHeader title="新規オーダー作成" subtitle="新しいオーダーを作成します" />
      <Container maxWidth="lg">
        <form onSubmit={handleSubmit}>
          <Box sx={{ mt: 4, p: 3, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
            {/* Customer Info Section */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>顧客情報</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth label="顧客の電話番号" value={customerPhoneSearch} onChange={handlePhoneInputChange}
                      placeholder="例: 090-1234-5678"
                      InputProps={{ endAdornment: isSearching ? <CircularProgress size={20} /> : null }}
                    />
                    {customerStatus && (
                      <Typography variant="body2" sx={{ mt: 1, color: customerStatus === '新規' ? 'text.secondary' : customerStatus.includes('NG：') ? 'error.main' : 'primary.main', fontWeight: customerStatus.includes('NG：') ? 'bold' : 'medium' }}>
                        {customerStatus}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} md={6} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Button variant="contained" color="primary" disabled={!customerPhoneSearch || !foundCustomer} onClick={handleOpenCustomerDialog} startIcon={<SearchIcon />}>
                      顧客情報を表示
                    </Button>
                    {customerStatus === '新規' && (
                      <Button variant="contained" color="secondary" onClick={handleCreateNewCustomer} startIcon={<PersonAddIcon />}>
                        顧客新規追加
                      </Button>
                    )}
                  </Grid>
                </Grid>
            </Box>

            {/* Reservation Info Section */}
            <Grid item xs={12}><Paper elevation={3} sx={{ p: 3, mb: 2 }}>
                <Typography variant="h6" gutterBottom>予約情報</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth required>
                        <InputLabel>予約タイプ</InputLabel>
                        <Select
                          value={formData.order_type}
                          label="予約タイプ"
                          onChange={(e) => setFormData({...formData, order_type: e.target.value})}
                        >
                          <MenuItem value="free">フリー</MenuItem>
                          <MenuItem value="nomination">指名</MenuItem>
                          <MenuItem value="direct_nomination">本指名</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth required>
                        <InputLabel>コース</InputLabel>
                        <Select
                          value={formData.course}
                          label="コース"
                          onChange={(e) => {
                            const courseId = e.target.value;
                            const selectedOption = coursesOptions.find(opt => opt.value === courseId);
                            setSelectedCourse(selectedOption ? selectedOption.course : null);
                            setFormData({...formData, course: courseId });
                          }}
                        >
                          {coursesOptions.map(course => (
                            <MenuItem key={course.value} value={course.value}>{course.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="予約日"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={formData.reservation_date}
                        onChange={(e) => setFormData({...formData, reservation_date: e.target.value})}
                        required
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="予約時間"
                        type="time"
                        InputLabelProps={{ shrink: true }}
                        value={formData.reservation_time}
                        onChange={(e) => setFormData({...formData, reservation_time: e.target.value})}
                        required
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="入室時間"
                        type="time"
                        InputLabelProps={{ shrink: true }}
                        value={formData.entry_time}
                        onChange={(e) => setFormData({...formData, entry_time: e.target.value})}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>ステータス</InputLabel>
                        <Select
                          value={formData.status}
                          label="ステータス"
                          onChange={(e) => setFormData({...formData, status: e.target.value})}
                        >
                          <MenuItem value="予約">予約</MenuItem>
                          <MenuItem value="完了">完了</MenuItem>
                          <MenuItem value="チェンジ">チェンジ</MenuItem>
                          <MenuItem value="キャンセル">キャンセル</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                </Grid>
            </Paper></Grid>

            {/* Cast/Driver Info Section */}
            <Grid item xs={12}><Paper elevation={3} sx={{ p: 3, mb: 2 }}>
                <Typography variant="h6" gutterBottom>キャスト・ドライバー情報</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>{renderUserSelect('cast')}</Grid>
                  <Grid item xs={12} md={6}>{renderUserSelect('driver')}</Grid>
                </Grid>
            </Paper></Grid>
            
            {/* Place Info Section */}
            <Grid item xs={12}><Paper elevation={3} sx={{ p: 3, mb: 2 }}>
                <Typography variant="h6" gutterBottom>場所情報</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    {/* 迎え場所：待機場所マスタ＋ホテル＋キャスト個別＋「迎え場所なし」の統合候補（かな検索対応） */}
                    <Autocomplete
                      options={pickupOptions}
                      value={selectedPickup}
                      onChange={(_e, newValue) => handleSelectPickup(newValue)}
                      filterOptions={filterPickupOptions}
                      getOptionLabel={(o) => (o?.source === 'none' ? o.name : `${o?.name || ''}${o?.name_kana ? `（${o.name_kana}）` : ''}`)}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          {option.source !== 'none' && (
                            <Chip label={pickupSourceLabel(option.source)} size="small" sx={{ mr: 1 }} />
                          )}
                          {option.name}{option.address ? ` / ${option.address}` : ''}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField {...params} label="迎え場所（かな検索対応）" required={!formData.pickup_no_location} />
                      )}
                    />
                    {!formData.pickup_no_location && (
                      <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'flex-start' }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="住所（登録に無い場所はここに入力）"
                          value={formData.pickup_address}
                          onChange={(e) => setFormData({ ...formData, pickup_address: e.target.value, pickup_lat: null, pickup_lng: null })}
                          helperText={
                            formData.pickup_lat != null && formData.pickup_lng != null
                              ? `座標: ${Number(formData.pickup_lat).toFixed(5)}, ${Number(formData.pickup_lng).toFixed(5)}`
                              : '住所を入力し「座標取得」を押すと位置を取得します'
                          }
                        />
                        <Button variant="outlined" onClick={handleGeocodePickup} disabled={pickupGeocoding} sx={{ whiteSpace: 'nowrap', mt: 0.5 }}>
                          {pickupGeocoding ? '取得中...' : '座標取得'}
                        </Button>
                      </Box>
                    )}
                    {formData.pickup_no_location && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        迎え場所なし（担当ドライバーと同じ位置から出発）
                      </Typography>
                    )}
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      options={allHotelOptions}
                      getOptionLabel={(option) => `${option.name} (${option.name_kana || ''})`}
                      filterOptions={filterHotelOptions}
                      onChange={(event, newValue) => {
                        if (newValue) {
                          setFormData(prev => ({
                            ...prev,
                            hotel_name: newValue.name,
                            hotel_id: newValue.id ?? null,
                            hotel_address: newValue.address,
                            delivery_address: newValue.address, // ここも更新
                            hotel_lat: newValue.latitude ?? null,
                            hotel_lng: newValue.longitude ?? null,
                          }));
                        } else {
                          setFormData(prev => ({
                            ...prev,
                            hotel_name: '',
                            hotel_id: null,
                            hotel_address: '',
                            delivery_address: '', // クリア
                            hotel_lat: null,
                            hotel_lng: null,
                          }));
                        }
                      }}
                      loading={loadingHotels}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="ホテル名（かな検索対応）"
                          error={ngHotelConflicts.length > 0}
                          helperText={
                            ngHotelConflicts.length > 0
                              ? `⚠ このホテルはNG登録されています：${ngHotelConflicts.join('、')}`
                              : undefined
                          }
                          onChange={(e) => {
                            // Autocompleteが内部で処理するので、ここでは何もしないか、
                            // もしくは選択されていない手入力のテキストをフォームデータに反映する
                            if (!hotelOptions.some(h => h.name === e.target.value)) {
                                setFormData(prev => ({...prev, hotel_name: e.target.value, hotel_id: null, hotel_address: ''}));
                            }
                          }}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingHotels ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                    {ngHotelConflicts.length > 0 && (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        選択中のホテルは次のキャストのNGホテルです：{ngHotelConflicts.join('、')}
                      </Alert>
                    )}
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="ホテル住所"
                      value={formData.delivery_address}
                      onChange={(e) => setFormData({...formData, delivery_address: e.target.value})}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="部屋番号"
                      placeholder="オーダー後に判明したら入力（後で編集も可）"
                      value={formData.room_number}
                      onChange={(e) => setFormData({ ...formData, room_number: e.target.value })}
                    />
                  </Grid>

                  {/* ルート・予測時間（担当ドライバー現在地→迎え場所→ホテル） */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <Button variant="outlined" onClick={handleCalcRoute} disabled={routeLoading}>
                        {routeLoading ? '計算中...' : '予測時間を計算'}
                      </Button>
                      <Typography variant="body2" color="text.secondary">
                        担当ドライバーの現在地→迎え場所→ホテルの所要時間を計算します（現在地が無い場合は迎え場所→ホテル）
                      </Typography>
                    </Box>
                    {routeError && (
                      <Alert severity="warning" sx={{ mt: 1 }}>{routeError}</Alert>
                    )}
                    {routeInfo && (
                      <Alert severity={routeInfo.estimated ? 'warning' : 'info'} sx={{ mt: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          合計 {fmtMinutes(routeInfo.duration)}（{(routeInfo.distance / 1000).toFixed(1)}km）
                          {routeInfo.estimated ? '（概算：直線距離）' : ''}
                        </Typography>
                        {!routeInfo.driverIncluded && (
                          <Typography variant="caption" color="text.secondary">
                            ※担当ドライバーの現在地が取得できなかったため、迎え場所→ホテルで計算しています
                          </Typography>
                        )}
                        {routeInfo.legLabels?.map((l: string, i: number) => (
                          <Typography key={i} variant="body2">・{l}</Typography>
                        ))}
                      </Alert>
                    )}
                  </Grid>
                </Grid>
            </Paper></Grid>

            {/* Other Info Section */}
            <Grid item xs={12}><Paper elevation={3} sx={{ p: 3, mb: 2 }}>
                <Typography variant="h6" gutterBottom>その他情報</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="割引額"
                      name="discount"
                      value={formData.discount || 0}
                      onChange={(e) => {
                        // 割引額を変更したら収益合計の手動編集を解除し、
                        // 「収益 - 割引額」で収益合計と合計金額を再計算させる
                        setIsManuallyEdited(prev => ({ ...prev, revenue: false }));
                        setFormData({...formData, discount: parseInt(e.target.value) || 0});
                      }}
                      InputProps={{
                        startAdornment: <span>¥</span>,
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="サービスタイム（分）"
                      name="service_time"
                      value={formData.service_time || 0}
                      onChange={(e) => setFormData({...formData, service_time: parseInt(e.target.value) || 0})}
                      InputProps={{
                        endAdornment: <span>分</span>,
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>オプション詳細</Typography>
                    
                    {/* オプション入力フォーム */}
                    {formData.option_details?.map((option, index) => (
                      <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              label="オプション項目"
                              value={option.name || ''}
                              onChange={(e) => {
                                const newOptions = [...formData.option_details];
                                newOptions[index].name = e.target.value;
                                setFormData({...formData, option_details: newOptions});
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <TextField
                              fullWidth
                              type="number"
                              label="キャストバック"
                              value={option.castback || 0}
                              onChange={(e) => {
                                const newOptions = [...formData.option_details];
                                newOptions[index].castback = parseInt(e.target.value) || 0;
                                setFormData({...formData, option_details: newOptions});
                              }}
                              InputProps={{
                                startAdornment: <span>¥</span>,
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <TextField
                              fullWidth
                              type="number"
                              label="収益"
                              value={option.profit || 0}
                              onChange={(e) => {
                                const newOptions = [...formData.option_details];
                                newOptions[index].profit = parseInt(e.target.value) || 0;
                                setFormData({...formData, option_details: newOptions});
                              }}
                              InputProps={{
                                startAdornment: <span>¥</span>,
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <IconButton 
                              color="error" 
                              onClick={() => {
                                const newOptions = formData.option_details.filter((_, i) => i !== index);
                                setFormData({...formData, option_details: newOptions});
                              }}
                            >
                              <CloseIcon />
                            </IconButton>
                          </Grid>
                        </Grid>
                      </Box>
                    ))}
                    
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        const newOptions = [...(formData.option_details || [])];
                        newOptions.push({ name: '', castback: 0, profit: 0 });
                        setFormData({...formData, option_details: newOptions});
                      }}
                      sx={{ mt: 1 }}
                    >
                      オプションを追加
                    </Button>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                      オプション
                    </Typography>
                    <FormGroup>
                      {optionChoices.map((option) => (
                        <FormControlLabel
                          key={option.id}
                          control={
                            <Checkbox
                              checked={formData.options.includes(option.id.toString())}
                              onChange={(e) => handleOptionChange(e.target.checked, option)}
                              name={option.name}
                            />
                          }
                          label={`${option.name} (${option.price}円)`}
                        />
                      ))}
                    </FormGroup>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="特記事項"
                      value={formData.special_instructions}
                      onChange={(e) => setFormData({...formData, special_instructions: e.target.value})}
                    />
                  </Grid>
                </Grid>
            </Paper></Grid>

            <Grid item xs={12}><Paper elevation={3} sx={{ p: 3, mb: 2 }}>
              <Typography variant="h6" gutterBottom>合計金額</Typography>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <TextField 
                    label="キャストバック合計" 
                    value={calculatedTotals.castback.toLocaleString()} 
                    fullWidth 
                    onChange={(e) => {
                      setIsManuallyEdited(prev => ({ ...prev, castback: true }));
                      const value = parseInt(e.target.value.replace(/,/g, '')) || 0;
                      setCalculatedTotals(prev => ({ ...prev, castback: value, grandTotal: value + prev.revenue }));
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField 
                    label="収益合計" 
                    value={calculatedTotals.revenue.toLocaleString()} 
                    fullWidth
                    onChange={(e) => {
                      setIsManuallyEdited(prev => ({ ...prev, revenue: true }));
                      const value = parseInt(e.target.value.replace(/,/g, '')) || 0;
                      setCalculatedTotals(prev => ({ ...prev, revenue: value, grandTotal: value + prev.castback }));
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField 
                    label="合計金額" 
                    value={calculatedTotals.grandTotal.toLocaleString()} 
                    fullWidth 
                    InputProps={{ readOnly: true }} 
                  />
                </Grid>
              </Grid>
            </Paper></Grid>

            <Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Box>
                <Button 
                  variant="outlined" 
                  color="secondary" 
                  size="large"
                  onClick={() => navigate('/orders')}
                  sx={{ mr: 2 }}
                >
                  キャンセル
                </Button>
                <Button 
                  variant="outlined" 
                  color="primary" 
                  size="large"
                  onClick={() => {
                    // フォームをクリア
                    setFormData({
                      customer_id: '',
                      customer_phone: '',
                      cast_ids: [],
                      user_id: '',
                      driver_id: '',
                      hotel_name: '',
                      hotel_id: null,
                      hotel_address: '',
                      room_number: '',
                      pickup_lat: null,
                      pickup_lng: null,
                      pickup_no_location: false,
                      hotel_lat: null,
                      hotel_lng: null,
                      reservation_date: '',
                      reservation_time: '',
                      order_type: 'free',
                      status: '予約',
                      course: '60分',
                      options: [],
                      entry_time: '',
                      discount: 0,
                      description: '',
                      special_instructions: '',
                      pickup_address: '',
                      delivery_address: '',
                      service_time: 0,
                      option_details: []
                    });
                    setCustomerPhoneSearch('');
                    setFoundCustomer(null);
                  }}
                >
                  クリア
                </Button>
              </Box>
              <Button 
                variant="contained" 
                color="primary" 
                type="submit"
                size="large"
              >
                オーダーを作成
              </Button>
            </Grid>
          </Box>
        </form>

        {/* Customer Info Dialog */}
        <Dialog open={isCustomerDialogOpen} onClose={() => setCustomerDialogOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle>顧客情報</DialogTitle>
            <DialogContent>
              {foundCustomer && (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <List>
                      <ListItem>
                        <ListItemText 
                          primary="名前" 
                          secondary={
                            foundCustomer.customer_name
                            || foundCustomer.full_name
                            || `${foundCustomer.last_name || ''} ${foundCustomer.first_name || ''}`.trim()
                            || foundCustomer.name
                            || foundCustomer.username
                            || '未設定'
                          } 
                        />
                      </ListItem>
                      <Divider />
                      <ListItem>
                        <ListItemText 
                          primary="電話番号" 
                          secondary={foundCustomer.phone_number || foundCustomer.phone || '未設定'} 
                        />
                      </ListItem>
                      <Divider />
                      <ListItem>
                        <ListItemText 
                          primary="住所" 
                          secondary={foundCustomer.address || '未設定'} 
                        />
                      </ListItem>
                    </List>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                      オーダー履歴
                    </Typography>
                    
                    <Box sx={{ height: 300, overflow: 'auto', border: '1px solid #eee', borderRadius: 1, p: 1 }}>
                      {loadingHistory ? (
                        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                          <CircularProgress size={30} />
                        </Box>
                      ) : orderHistory.length > 0 ? (
                        <List>
                          {orderHistory.map((order, index) => (
                            <React.Fragment key={order.id || index}>
                              <ListItem alignItems="flex-start">
                                <ListItemText
                                  primary={
                                    <Typography variant="body1" fontWeight="medium">
                                      {order.entry_time
                                        ? new Date(order.entry_time).toLocaleString('ja-JP', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
                                        : (order.entry_time_value || '入室時間未設定')}
                                      {order.course_duration ? ` - ${order.course_duration}分` : ''}
                                    </Typography>
                                  }
                                  secondary={
                                    <>
                                      <Typography variant="body2" component="span" color="text.primary">
                                        キャスト: {order.cast_name || '指定なし'}
                                      </Typography>
                                      <br />
                                      <Typography variant="body2" component="span" color="text.primary">
                                        予約タイプ: {
                                          order.order_type === 'free' ? 'フリー' : 
                                          order.order_type === 'nomination' ? '指名' : 
                                          order.order_type === 'direct_nomination' ? '本指名' : 
                                          order.order_type || '不明'
                                        }
                                      </Typography>
                                      <br />
                                      <Typography variant="body2" component="span">
                                        {order.hotel_name && `場所: ${order.hotel_name}`}
                                        {order.delivery_address && ` (${order.delivery_address})`}
                                      </Typography>
                                      {order.special_instructions && (
                                        <>
                                          <br />
                                          <Typography variant="body2" component="span">
                                            特記事項: {order.special_instructions}
                                          </Typography>
                                        </>
                                      )}
                                    </>
                                  }
                                />
                              </ListItem>
                              {index < orderHistory.length - 1 && <Divider />}
                            </React.Fragment>
                          ))}
                        </List>
                      ) : (
                        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                          <Typography variant="body1" color="text.secondary">
                            注文履歴はありません
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCustomerDialogOpen(false)} color="primary">
                閉じる
              </Button>
            </DialogActions>
        </Dialog>
      </Container>
    </DashboardLayout>
  );
};

export default CreateOrder;